import { useState, useCallback, useEffect, useRef } from 'react';
import { getUnseenNotificationCounts } from '../services/notification';

interface NotificationCounts {
  organizer: number;
  challenger: number;
  audience: number;
  total: number;
}

interface UseNotificationCountsReturn {
  counts: NotificationCounts;
  loading: boolean;
  error: string | null;
  refreshCounts: () => Promise<void>;
}

export const useNotificationCounts = (accessToken?: string): UseNotificationCountsReturn => {
  const [counts, setCounts] = useState<NotificationCounts>({
    organizer: 0,
    challenger: 0,
    audience: 0,
    total: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialized = useRef(false);

  const refreshCounts = useCallback(async () => {
    if (!accessToken) {
      setCounts({ organizer: 0, challenger: 0, audience: 0, total: 0 });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getUnseenNotificationCounts(accessToken);
      
      if (response.statusCode === 200) {
        setCounts(response.body);
      } else {
        setCounts(response.body);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch counts');
      setCounts({ organizer: 0, challenger: 0, audience: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken && !isInitialized.current) {
      isInitialized.current = true;
      refreshCounts();
    }
  }, [accessToken, refreshCounts]);

  useEffect(() => {
    if (!accessToken) {
      setCounts({ organizer: 0, challenger: 0, audience: 0, total: 0 });
      setError(null);
      isInitialized.current = false;
    }
  }, [accessToken]);

  return {
    counts,
    loading,
    error,
    refreshCounts
  };
};

export default useNotificationCounts;